import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import IconGift from "@/assets/icons/gift.svg?react";
import Image from "@/components/base/Image.tsx";
import type { Voucher } from "../api";

interface VoucherHeroProps {
  voucher: Voucher;
}

/**
 * Detail-sheet hero banner for a voucher.
 *
 * - 0 images (no icon, no gallery) → orange-gradient placeholder with gift icon
 * - 1 image (icon OR a single gallery entry) → static banner
 * - 2+ images → Swiper slider with pagination dots
 */
export default function VoucherHero({ voucher }: VoucherHeroProps) {
  // Prefer the explicit gallery when present, otherwise fall back to the
  // single icon so legacy vouchers without `images` still render a banner.
  const slides =
    voucher.images && voucher.images.length > 0
      ? voucher.images
      : voucher.icon
      ? [voucher.icon]
      : [];

  const showHotBadge = voucher.is_hot;

  return (
    <div className="relative w-full aspect-[16/10] bg-white/[0.04] overflow-hidden mb-4">
      {slides.length === 0 ? (
        <div className="absolute inset-0 flex-center bg-gradient-to-br from-orange-100/20 via-gray-4 to-gray-4">
          <IconGift
            className="size-16 text-orange-100/60"
            strokeWidth="1.5"
          />
        </div>
      ) : slides.length === 1 ? (
        <div className="absolute inset-0">
          <Image
            urls={slides[0]}
            size="medium"
            imgClassName="w-full h-full object-cover"
            wrapperClassName="w-full h-full"
            alt={voucher.title}
          />
        </div>
      ) : (
        <Swiper
          modules={[Pagination]}
          pagination={{ clickable: true }}
          loop={slides.length > 2}
          spaceBetween={0}
          slidesPerView={1}
          className="voucher-hero-swiper w-full h-full"
        >
          {slides.map((img, i) => (
            <SwiperSlide key={i} className="!w-full !h-full">
              <div className="w-full h-full">
                <Image
                  urls={img}
                  size="medium"
                  imgClassName="w-full h-full object-cover"
                  wrapperClassName="w-full h-full"
                  alt={`${voucher.title} ${i + 1}`}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {showHotBadge && (
        <span className="absolute top-3 right-3 z-10 px-2 py-0.5 rounded-full bg-red text-[10px] font-bold text-white leading-none shadow-[0_2px_8px_rgba(255,53,53,0.5)]">
          HOT
        </span>
      )}

      {/* Pagination styling tweaks — scoped with a class on Swiper */}
      <style>{`
        .voucher-hero-swiper .swiper-pagination-bullet {
          background: rgba(255, 255, 255, 0.5);
          opacity: 1;
        }
        .voucher-hero-swiper .swiper-pagination-bullet-active {
          background: #FBC76C;
        }
      `}</style>
    </div>
  );
}
